import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { api, inr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useIsBlinkit } from "../lib/storefrontTheme";
import { cartShopLabel } from "../lib/multiShop";
import BkCartBar from "./blinkit/BkCartBar";

// Exact-path hides. "/orders" (My Orders tab) and "/profile" (Account
// tab) are non-shopping contexts — user-requested: the cart bar should
// only surface on the shopping tabs (Home / Catalog / Shops), not here.
const HIDE_ON = new Set(["/cart", "/checkout", "/orders", "/profile"]);

export default function StickyCart() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [cart, setCart] = useState(null);
    const blinkit = useIsBlinkit();

    const fetchCart = async () => {
        if (!user) return setCart(null);
        try {
            const { data } = await api.get("/cart");
            if (data?.items?.length > 0) setCart(data);
            else setCart(null);
        } catch {
            setCart(null);
        }
    };

    useEffect(() => {
        fetchCart();
        const id = setInterval(fetchCart, 5000);
        const onUpdate = () => fetchCart();
        window.addEventListener("cart:update", onUpdate);
        return () => {
            clearInterval(id);
            window.removeEventListener("cart:update", onUpdate);
        };
        // eslint-disable-next-line
    }, [user]);

    if (!cart || !cart.items?.length) return null;
    if (HIDE_ON.has(pathname)) return null;
    // Hide on PDP — the page has its own sticky ADD TO CART bar.
    if (pathname.startsWith("/product/")) return null;
    // Sub-pages reached from the Orders/Account tabs (order tracking,
    // profile edit) are the same non-shopping context as their parent
    // tab — hide there too, same reasoning as the exact-path hides above.
    if (pathname.startsWith("/orders/") || pathname.startsWith("/order-groups/") || pathname.startsWith("/profile/")) return null;
    // A support-ticket conversation has its own reply bar at the bottom (Location Service Engine).
    if (pathname.startsWith("/support/tickets/")) return null;
    const count = cart.items.reduce((s, i) => s + i.quantity, 0);
    // Blinkit-theme tenants get the themed bar; every other theme renders exactly the bar below.
    if (blinkit) return <BkCartBar cart={cart} count={count} />;
    return (
        <Link
            to="/cart"
            data-testid="sticky-cart-bar"
            // From lg (1024px) up there is no bottom tab bar (desktop top navigation instead): ``desktop-dock`` (index.css)
            // turns the bar into a compact floating cart at the content column's bottom-right. Phones/tablets unchanged.
            // ``bottom-[68px]`` is the fallback: a WebView too old to parse CSS max() ignores the inline value below and
            // keeps the bar where it has always been, instead of leaving a fixed bar with no bottom at all.
            className="sticky-cart desktop-dock fixed bottom-[68px] left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] bg-primary text-primary-foreground rounded-2xl flex items-center justify-between px-4 py-3.5 shadow-2xl"
            // 68px above the bottom edge, as before — plus the home-indicator inset where there is one. BottomNav pads
            // its own bottom by env(safe-area-inset-bottom), so on iPhones with a home indicator it is ~26px taller and
            // a fixed 68px offset overlapped its icons. With no inset this resolves to exactly 68px (unchanged).
            style={{ maxWidth: "calc(var(--shell-width) - 24px)", bottom: "max(68px, calc(67px + env(safe-area-inset-bottom, 0px)))" }}
        >
            <div className="flex items-center gap-3">
                <div className="relative">
                    <ShoppingBag size={22} />
                    <span className="absolute -top-1.5 -right-1.5 bg-card text-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-primary">
                        {count}
                    </span>
                </div>
                <div className="text-sm">
                    <div className="font-semibold">{count} item{count > 1 ? "s" : ""} • {cartShopLabel(cart)}</div>
                    <div className="text-[11px] opacity-90">{inr(cart.totals?.grand_total)} • EXTRA TAX</div>
                </div>
            </div>
            <div data-testid="sticky-cart-checkout" className="text-sm font-bold uppercase tracking-wider">View Cart →</div>
        </Link>
    );
}
